<template>
    <div class="post wrapper">
        <PostNav />
        <div class="markdown-content">
            <hr />
            <PostHead />
            <div class="markdown-body" v-html="compiledMarkdown">
            </div>
            <router-view />
            <PostFoot />
            <PostSlider />
        </div>
    </div>
</template>

<script setup>
import { marked } from 'marked';
import matter from 'gray-matter'
import { onMounted, ref, watch, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { readingTime } from 'reading-time-estimator';

import PostFoot from './PostFoot.vue';
import PostHead from './PostHead.vue';
import PostNav from './PostNav.vue';
import PostSlider from './PostSlider.vue';

// Add Buffer polyfill for browser environment
// This needs to be done before using gray-matter
if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
    window.Buffer = {
        from: (value, encoding) => {
            if (encoding === 'utf8' || encoding === 'utf-8') {
                return new TextEncoder().encode(value);
            }
            return value;
        },
        isBuffer: (obj) => false
    };
}

const { locale } = useI18n()
const route = useRoute()
const compiledMarkdown = ref('')
const postHeadProps = ref({ author: '', pubDate: '', readTime: '' });

provide('postHeadProps', postHeadProps);

const loadMarkdown = async () => {
    try {
        console.log('Loading markdown for:', route.params.id, 'in locale:', locale.value)
        const markdownModule = await import(`../../assets/markdowns/blog/${locale.value}/${route.params.id}.md?raw`);

        const rawMarkdown = markdownModule.default;

        const { content, data } = matter(rawMarkdown);

        postHeadProps.value = data;
        postHeadProps.value.readTime = readingTime(content).minutes;
        compiledMarkdown.value = marked.parse(content);
    } catch (error) {
        console.error('Error loading markdown:', error)
        compiledMarkdown.value = `<div class="error">Error loading content: ${error.message}</div>`
    }
}



onMounted(() => { loadMarkdown() })

watch([() => locale.value, () => route.params.id], loadMarkdown)
</script>

<style>
.post {
    font-family: var(--MainFontFamily);
    padding: 0 0 6rem 0 !important;

    h1,
    h2,
    p {
        color: var(--ComplementaryColor04) !important;
        margin: 1rem 0;
    }

    ul,
    ol {
        margin: 0 4rem;
    }
}

.markdown-content {
    padding: 0 3rem;
    max-width: 100vw;
    margin: 0 auto;
}

hr {
    margin: 0 0 6rem 0;
    border: 1px solid #43485637;
    background-color: #43485637;
}

.markdown-body {
    padding: 0 0 6rem 0;

    p,
    li {
        color: var(--ComplementaryColor04) !important;
        font-size: 2rem;
        line-height: 1.6;
    }

    li::marker {
        color: var(--SecondaryColor);
        font-weight: 600;
    }

    hr {
        margin: 0;
    }
}

img {
    max-width: 100%;
    height: auto;
    margin: 0 auto;
    display: block;
    border-radius: 1rem;
}

@media only screen and (max-width: 768px) {
    .post ol, .post ul {
        margin-inline: 1.6rem;
    }
    .post h1 {
        font-size: 2.4rem;
        line-height: 1.2;
    }
    .post h2 {
        font-size: 2rem;
        line-height: 1.2;
    }
    .markdown-body p, .markdown-body li {
        font-size: 1.8rem;
        line-height: 1.2;
        margin-bottom: 1.2rem;
    }
}
</style>